import { useMemo, useState } from "react";
import { clsx } from "clsx";
import {
  Activity,
  AlertCircle,
  Cpu,
  HardDrive,
  Layers,
  MemoryStick,
  Search,
  Server,
} from "lucide-react";
import { DashboardEmbed } from "@/components/monitoring/DashboardEmbed";
import { ResourceGauges } from "@/components/monitoring/ResourceGauges";
import { AlertRules } from "@/components/monitoring/AlertRules";
import { PersistedAlerts } from "@/components/monitoring/PersistedAlerts";
import { useNodeMetrics, useCapacity, useAlerts } from "@/hooks/useCapacity";
import { useVMs } from "@/hooks/useVMs";
import { useBackups } from "@/hooks/useBackups";
import { useActiveTenant } from "@/components/tenant/TenantSelector";
import type { CapacityInfo } from "@/types";

function KpiCard({
  label,
  value,
  sub,
  icon,
  tone = "neutral",
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  tone?: "neutral" | "good" | "warn" | "bad";
}) {
  const toneRing: Record<string, string> = {
    neutral: "border-gray-700",
    good: "border-emerald-700/60",
    warn: "border-amber-700/60",
    bad: "border-red-700/60",
  };
  return (
    <div
      className={clsx(
        "rounded-lg border bg-gray-900/60 p-4",
        toneRing[tone],
      )}
    >
      <div className="flex items-center justify-between text-gray-400 text-xs uppercase tracking-wide">
        <span>{label}</span>
        <span>{icon}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-100">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

function pct(used: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.max(0, (used / total) * 100));
}

const NODE_STATUS_BADGE: Record<string, string> = {
  online: "bg-emerald-600/20 text-emerald-300",
  maintenance: "bg-amber-600/20 text-amber-300",
  offline: "bg-red-600/20 text-red-300",
};

function NodesTable({ capacity }: { capacity: CapacityInfo[] }) {
  if (!capacity.length) {
    return <div className="text-sm text-gray-500">No nodes reported.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-800">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-900/80 text-xs uppercase tracking-wide text-gray-400">
          <tr>
            <th className="px-4 py-2 text-left">Node</th>
            <th className="px-4 py-2 text-center">Status</th>
            <th className="px-4 py-2 text-right">CPU</th>
            <th className="px-4 py-2 text-right">RAM</th>
            <th className="px-4 py-2 text-right">Disk</th>
            <th className="px-4 py-2 text-right">VMs</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {capacity.map((n) => {
            const cpuPct = pct(n.used_cpu, n.total_cpu);
            const ramTotalGb = n.total_ram_mb / 1024;
            const ramUsedGb = n.used_ram_mb / 1024;
            const ramPct = pct(n.used_ram_mb, n.total_ram_mb);
            const diskPct = pct(n.used_disk_gb, n.total_disk_gb);
            const offline = n.status !== "online";
            return (
              <tr
                key={n.node}
                className={clsx(
                  "hover:bg-gray-900/40",
                  offline ? "text-gray-500 opacity-60" : "text-gray-200",
                )}
              >
                <td className="px-4 py-2 font-medium">{n.node}</td>
                <td className="px-4 py-2 text-center">
                  <span
                    className={clsx(
                      "rounded px-2 py-0.5 text-xs font-medium",
                      NODE_STATUS_BADGE[n.status] ?? "bg-gray-600/20 text-gray-400",
                    )}
                  >
                    {n.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  {n.used_cpu.toFixed(1)}/{n.total_cpu} ({cpuPct.toFixed(0)}%)
                </td>
                <td className="px-4 py-2 text-right">
                  {ramUsedGb.toFixed(1)}/{ramTotalGb.toFixed(0)} GB ({ramPct.toFixed(0)}%)
                </td>
                <td className="px-4 py-2 text-right">
                  {n.used_disk_gb.toFixed(0)}/{n.total_disk_gb.toFixed(0)} GB ({diskPct.toFixed(0)}%)
                </td>
                <td className="px-4 py-2 text-right">{n.vm_count}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const STATUS_BADGE: Record<string, string> = {
  running: "bg-emerald-600/20 text-emerald-300",
  stopped: "bg-gray-600/20 text-gray-400",
  paused: "bg-amber-600/20 text-amber-300",
  suspended: "bg-amber-600/20 text-amber-300",
  migrating: "bg-sky-600/20 text-sky-300",
  error: "bg-red-600/20 text-red-300",
  provisioning: "bg-violet-600/20 text-violet-300",
};

function VMsTable({ search }: { search: string }) {
  const { data: vms = [], isLoading } = useVMs();
  const { data: backups = [] } = useBackups();

  const lastBackupByVm = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of backups) {
      if (b.status !== "completed") continue;
      const cur = m.get(b.vm_id);
      if (!cur || new Date(b.created_at) > new Date(cur)) m.set(b.vm_id, b.created_at);
    }
    return m;
  }, [backups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vms;
    return vms.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        String(v.vmid).includes(q) ||
        v.node.toLowerCase().includes(q),
    );
  }, [vms, search]);

  if (isLoading) {
    return <div className="py-8 text-center text-gray-500">Loading VMs...</div>;
  }
  if (!filtered.length) {
    return <div className="text-sm text-gray-500">No VMs match.</div>;
  }

  const fmtBackup = (iso?: string) => {
    if (!iso) return <span className="text-red-400">never</span>;
    const d = new Date(iso);
    const ageH = (Date.now() - d.getTime()) / 36e5;
    const stale = ageH > 36;
    return (
      <span className={stale ? "text-amber-400" : "text-gray-300"}>
        {d.toISOString().slice(0, 16).replace("T", " ")}Z
      </span>
    );
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-800">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-900/80 text-xs uppercase tracking-wide text-gray-400">
          <tr>
            <th className="px-4 py-2 text-left">VMID</th>
            <th className="px-4 py-2 text-left">Name</th>
            <th className="px-4 py-2 text-left">Node</th>
            <th className="px-4 py-2 text-center">Tier</th>
            <th className="px-4 py-2 text-center">Status</th>
            <th className="px-4 py-2 text-right">Cores</th>
            <th className="px-4 py-2 text-right">RAM</th>
            <th className="px-4 py-2 text-right">Disk</th>
            <th className="px-4 py-2 text-left">Last backup</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {filtered.map((vm) => {
            const offline = vm.status !== "running";
            const lastB = lastBackupByVm.get(vm.id);
            return (
              <tr
                key={vm.id}
                className={clsx(
                  "hover:bg-gray-900/40",
                  offline ? "text-gray-500 opacity-60" : "text-gray-200",
                )}
              >
                <td className="px-4 py-2 font-mono text-xs">{vm.vmid}</td>
                <td className="px-4 py-2">{vm.name}</td>
                <td className="px-4 py-2 text-xs">{vm.node}</td>
                <td className="px-4 py-2 text-center font-semibold">{vm.service_tier}</td>
                <td className="px-4 py-2 text-center">
                  <div className="flex flex-wrap items-center justify-center gap-1">
                    <span
                      className={clsx(
                        "rounded px-2 py-0.5 text-xs font-medium",
                        STATUS_BADGE[vm.status] ?? "bg-gray-600/20 text-gray-400",
                      )}
                    >
                      {vm.status}
                    </span>
                    {vm.is_template && (
                      <span className="rounded bg-sky-600/20 px-2 py-0.5 text-xs font-medium text-sky-300">
                        template
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-right">{vm.cores}</td>
                <td className="px-4 py-2 text-right">{(vm.memory_mb / 1024).toFixed(1)} GB</td>
                <td className="px-4 py-2 text-right">{vm.disk_gb} GB</td>
                <td className="px-4 py-2">{fmtBackup(lastB)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Monitoring() {
  const [tenantId] = useActiveTenant();
  const { data: nodeMetrics, isLoading: nodesLoading } = useNodeMetrics();
  const { data: capacity = [] } = useCapacity();
  const { data: alerts = [] } = useAlerts();
  const { data: vms = [] } = useVMs();
  const [vmSearch, setVmSearch] = useState("");

  const aggregate = useMemo(() => {
    const totals = capacity.reduce(
      (acc, n) => ({
        cores: acc.cores + n.total_cpu,
        cores_used: acc.cores_used + n.used_cpu,
        ram: acc.ram + n.total_ram_mb,
        ram_used: acc.ram_used + n.used_ram_mb,
        disk: acc.disk + n.total_disk_gb,
        disk_used: acc.disk_used + n.used_disk_gb,
      }),
      { cores: 0, cores_used: 0, ram: 0, ram_used: 0, disk: 0, disk_used: 0 },
    );
    const realVms = vms.filter((v) => !v.is_template);
    const running = realVms.filter((v) => v.status === "running").length;
    const templates = vms.filter((v) => v.is_template).length;
    const openAlerts = alerts.filter(
      (a) => a.severity !== "info" && !a.resolved_at,
    ).length;
    return {
      nodes: capacity.length,
      vms_total: realVms.length,
      vms_running: running,
      vms_templates: templates,
      cpu_pct: pct(totals.cores_used, totals.cores),
      ram_pct: pct(totals.ram_used, totals.ram),
      disk_pct: pct(totals.disk_used, totals.disk),
      alerts: openAlerts,
    };
  }, [capacity, vms, alerts]);

  const tone = (p: number): "good" | "warn" | "bad" =>
    p >= 90 ? "bad" : p >= 75 ? "warn" : "good";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Monitoring</h1>
        <p className="mt-1 text-sm text-gray-400">
          Cluster overview, per-node and per-VM views, alerts.
        </p>
      </div>

      {/* AGGREGATE KPI */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label="Nodes"
          value={aggregate.nodes}
          icon={<Server size={16} />}
        />
        <KpiCard
          label="VMs running"
          value={`${aggregate.vms_running}/${aggregate.vms_total}`}
          sub={
            aggregate.vms_templates > 0
              ? `+${aggregate.vms_templates} template${aggregate.vms_templates > 1 ? "s" : ""}`
              : aggregate.vms_total > 0
                ? `${aggregate.vms_total - aggregate.vms_running} stopped`
                : undefined
          }
          icon={<Layers size={16} />}
          tone={
            aggregate.vms_total === 0
              ? "neutral"
              : aggregate.vms_running === aggregate.vms_total
                ? "good"
                : "warn"
          }
        />
        <KpiCard
          label="Cluster CPU"
          value={`${aggregate.cpu_pct.toFixed(0)}%`}
          icon={<Cpu size={16} />}
          tone={tone(aggregate.cpu_pct)}
        />
        <KpiCard
          label="Cluster RAM"
          value={`${aggregate.ram_pct.toFixed(0)}%`}
          icon={<MemoryStick size={16} />}
          tone={tone(aggregate.ram_pct)}
        />
        <KpiCard
          label="Cluster Disk"
          value={`${aggregate.disk_pct.toFixed(0)}%`}
          icon={<HardDrive size={16} />}
          tone={tone(aggregate.disk_pct)}
        />
        <KpiCard
          label="Open alerts"
          value={aggregate.alerts}
          icon={<AlertCircle size={16} />}
          tone={aggregate.alerts === 0 ? "good" : aggregate.alerts > 5 ? "bad" : "warn"}
        />
      </div>

      {/* NODES SECTION */}
      <div>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-200">
          <Server size={18} /> Nodes
        </h2>
        {nodesLoading ? (
          <div className="py-8 text-center text-gray-500">Loading nodes...</div>
        ) : (
          <div className="space-y-4">
            <ResourceGauges nodes={nodeMetrics ?? []} />
            <NodesTable capacity={capacity} />
          </div>
        )}
      </div>

      {/* VMs SECTION */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-200">
            <Layers size={18} /> Virtual Machines
            <span className="text-sm font-normal text-gray-500">
              ({aggregate.vms_running}/{aggregate.vms_total} running)
            </span>
          </h2>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="search"
              placeholder="Filter (name, vmid, node)..."
              value={vmSearch}
              onChange={(e) => setVmSearch(e.target.value)}
              className="rounded border border-gray-700 bg-gray-900 py-1 pl-7 pr-3 text-sm text-gray-200 focus:border-sky-600 focus:outline-none"
            />
          </div>
        </div>
        <VMsTable search={vmSearch} />
      </div>

      {/* GRAFANA */}
      <div>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-200">
          <Activity size={18} /> Grafana
        </h2>
        <DashboardEmbed
          tenantId={tenantId != null ? String(tenantId) : undefined}
        />
      </div>

      {/* ALERT RULES */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-gray-200">Alert Rules</h2>
        <AlertRules />
      </div>

      {/* ALERTS DB */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-gray-200">Alerts</h2>
        <PersistedAlerts />
      </div>
    </div>
  );
}
